import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, CheckCircle, Mail, Phone, Search, RotateCcw } from "lucide-react";

interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  user_type: string;
}

export function AdminVerificationReset() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [verificationType, setVerificationType] = useState<"email" | "phone" | "both">("both");
  const [isSearching, setIsSearching] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const { toast } = useToast();

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      toast({
        title: "Search query required",
        description: "Please enter an email or name to search for users",
        variant: "destructive"
      });
      return;
    }

    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, user_id, email, full_name, phone, email_verified, phone_verified, user_type')
        .or(`email.ilike.%${searchQuery}%,full_name.ilike.%${searchQuery}%`)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setSearchResults(data || []);
      if (data?.length === 0) {
        toast({
          title: "No users found",
          description: "No users match your search query"
        });
      }
    } catch (error: any) {
      toast({
        title: "Search failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };

  const resetVerification = async () => {
    if (!selectedUser) {
      toast({
        title: "No user selected",
        description: "Please select a user first",
        variant: "destructive"
      });
      return;
    }

    setIsResetting(true);
    try {
      const { error } = await supabase.rpc('admin_reset_user_verification', {
        target_user_id: selectedUser.user_id,
        verification_type: verificationType
      });

      if (error) throw error;

      toast({
        title: "Verification reset successfully",
        description: `${verificationType === 'both' ? 'Email and phone' : verificationType} verification has been reset for ${selectedUser.email}`,
      });

      // Refresh the selected user's data
      const { data } = await supabase
        .from('profiles')
        .select('id, user_id, email, full_name, phone, email_verified, phone_verified, user_type')
        .eq('id', selectedUser.id)
        .single();
      
      if (data) {
        setSelectedUser(data);
        // Update search results if user is in current results
        setSearchResults(prev => prev.map(user => 
          user.id === data.id ? data : user
        ));
      }
    } catch (error: any) {
      toast({
        title: "Reset failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsResetting(false);
    }
  };

  const getVerificationStatus = (user: Profile) => {
    const statuses = [];
    if (user.email_verified) {
      statuses.push(<Badge key="email" variant="default" className="bg-green-100 text-green-800"><Mail className="w-3 h-3 mr-1" />Email ✓</Badge>);
    } else {
      statuses.push(<Badge key="email" variant="destructive"><Mail className="w-3 h-3 mr-1" />Email ✗</Badge>);
    }
    
    if (user.phone_verified) {
      statuses.push(<Badge key="phone" variant="default" className="bg-green-100 text-green-800"><Phone className="w-3 h-3 mr-1" />Phone ✓</Badge>);
    } else {
      statuses.push(<Badge key="phone" variant="destructive"><Phone className="w-3 h-3 mr-1" />Phone ✗</Badge>);
    }
    
    return statuses;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            Reset User Verification
          </CardTitle>
          <CardDescription>
            Search for users and reset their email or phone verification status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search Section */}
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="search">Search Users</Label>
                <Input
                  id="search"
                  placeholder="Enter email or full name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                />
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={searchUsers} 
                  disabled={isSearching}
                  className="flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {isSearching ? "Searching..." : "Search"}
                </Button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2">
                <Label>Search Results</Label>
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                        selectedUser?.id === user.id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                      onClick={() => setSelectedUser(user)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{user.full_name || 'No name'}</div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                          <div className="text-xs text-gray-500 capitalize">{user.user_type}</div>
                        </div>
                        <div className="flex flex-col gap-1">
                          {getVerificationStatus(user)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Selected User & Reset Section */}
          {selectedUser && (
            <div className="border-t pt-6 space-y-4">
              <div>
                <Label>Selected User</Label>
                <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="font-medium">{selectedUser.full_name || 'No name'}</div>
                      <div className="text-sm text-gray-600">{selectedUser.email}</div>
                      <div className="text-xs text-gray-500 capitalize">{selectedUser.user_type}</div>
                      {selectedUser.phone && (
                        <div className="text-xs text-gray-500">{selectedUser.phone}</div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div>Current Status:</div>
                      <div className="flex flex-col gap-1">
                        {getVerificationStatus(selectedUser)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="verification-type">Reset Type</Label>
                  <Select value={verificationType} onValueChange={(value: "email" | "phone" | "both") => setVerificationType(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both Email & Phone</SelectItem>
                      <SelectItem value="email">Email Only</SelectItem>
                      <SelectItem value="phone">Phone Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={resetVerification}
                    disabled={isResetting}
                    variant="destructive"
                    className="w-full flex items-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    {isResetting ? "Resetting..." : "Reset Verification"}
                  </Button>
                </div>
              </div>

              <div className="text-sm text-gray-600 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <strong>Warning:</strong> This will reset the verification status and clear any pending verification codes. 
                The user will need to verify their {verificationType === 'both' ? 'email and phone' : verificationType} again.
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}